import { CalendarClock, ShieldCheck, Zap } from "@tamagui/lucide-icons";
import { XStack, YStack } from "tamagui";

import { AssistantClientEvent } from "@/api/model/Conversation";
import TalkToDoctorHero from "@/assets/images/landing_page/v3/talk-to-doctor-hero.png";
import { Button, Heading, Image, Paragraph } from "@/components";
import {
  type ZocdocBookingContent,
  useZocdocBookingStore,
} from "@/store/zocdocBooking";
import { AssistantMessageTypes } from "@/types";
import { ZocdocLogo } from "./ZocdocLogo";

export type { ZocdocBookingContent };

export interface ZocdocBookingProps extends AssistantClientEvent {
  content: ZocdocBookingContent;
  type: AssistantMessageTypes.ZOCDOC_BOOKING;
}

const VALUE_ADDS = [
  { Icon: ShieldCheck, text: "In-network providers" },
  { Icon: CalendarClock, text: "Real-time availability" },
  { Icon: Zap, text: "Book in minutes" },
];

const ZocdocBooking = ({ id, content }: ZocdocBookingProps) => {
  const openForContent = useZocdocBookingStore((s) => s.openForContent);

  return (
    <YStack gap="$200">
      <XStack
        borderRadius="$150"
        backgroundColor="$fillCallout"
        outlineWidth={2}
        outlineColor="$strokeNeutral"
        outlineStyle="solid"
        overflow="hidden"
      >
        <YStack
          width="45%"
          backgroundColor="$fillNeutral"
          minHeight={180}
          $maxLg={{ display: "none" }}
        >
          <Image
            source={TalkToDoctorHero}
            style={{ width: "100%", height: "100%" }}
            objectFit="cover"
          />
        </YStack>
        <YStack
          flex={1}
          padding="$300"
          gap="$200"
          justifyContent="space-between"
        >
          <ZocdocLogo width={128} position="absolute" top="$300" right="$300" />
          <YStack gap="$150">
            <Heading size="$md">See a doctor in person</Heading>
            <YStack gap="$75">
              {VALUE_ADDS.map(({ Icon, text }) => (
                <XStack key={text} alignItems="center" gap="$100">
                  <Icon size={16} />
                  <Paragraph size="$sm">{text}</Paragraph>
                </XStack>
              ))}
            </YStack>
          </YStack>
          <YStack gap="$100">
            <Button
              mode="primary"
              dataTrackingId="zocdoc-open-booking-with-insurance"
              onPress={() => openForContent(content, id)}
            >
              Get started with insurance
            </Button>
            <Button
              mode="tertiary"
              dataTrackingId="zocdoc-open-booking-without-insurance"
              onPress={() => openForContent(content, id, "zip")}
            >
              Get started without insurance
            </Button>
          </YStack>
        </YStack>
      </XStack>
      {content.final && (
        <Paragraph conversational>
          Hopefully your appointment booking went okay. Those appointments will
          be managed through Zocdoc but we are always happy to answer any
          questions or provide assistance how we can.
        </Paragraph>
      )}
    </YStack>
  );
};

export default ZocdocBooking;
